/* global UpNextStreamOverrideEnabled, UpNextToastEnabled */
import React, { Component } from 'react';
import ReactTimeout from 'react-timeout';
import { browserHistory } from 'react-router';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import Loader from 'components/Loader';
import { getCookie } from 'helpers/Storage';
import { Endcard, UpNextToast, VideoDetail } from 'components/VideoPlayerGlobal/components';
import { getLiveVideo, getVideoDetailProps } from 'components/VideoPlayerGlobal/Helper';
import { noop } from 'helpers/GlobalHelper';
import { endcardVideoProcessor } from 'helpers/VideoProcessorHelper';
import {
  SITE_PLATFORM_WEB_LARGE,
  SITE_PLATFORM_WEB,
  SITE_PLATFORM_TABLET,
  SITE_PLATFORM_MOBILE,
  isMobileBreakpoint,
} from 'helpers/BreakpointHelper';
import { getCommonTealiumProps } from 'helpers/AccountHelper';
import {
  LIVE_EPG_THUMBNAIL_IMAGE,
  LIVE_PLAYER_BACKGROUND_IMAGE,
  WATCH_RELATED_THUMBNAIL_IMAGE,
  getDynamicImage,
  renderImage,
} from 'helpers/ImageHelper';
import {
  geolocationEnabled,
  isEpgv2Enabled,
  isFNCv2Enabled,
  upNextLiveEventsEnabled,
} from 'helpers/ConfigHelper';
import { RESTART_TYPE_LIVE } from 'screens/Watch/WatchHelpers';
import {
  DEBUG_PANEL_TIMEOUT_DISABLED,
  FOX,
  getAuthZEntitlementIssues,
  isSportsContent,
} from 'helpers/ContentHelper';
import {
  now,
  stringToDate,
  isBefore,
  subMinutes,
  differenceInMilliseconds,
} from 'helpers/DateTimeHelper';
import { getLocation as getGeoLocation } from 'helpers/GeoLocationHelper';
import { logPlayerError, logPlayerEvent } from 'helpers/LogReportHelpers';
import {
  LIVE,
  INTERRUPTION_METHODS,
  PLAYER_CONTEXT_LIVE,
  getActivePlayer,
  getPlayerState,
  isPlayerTypeHero,
  playerHideControls,
  playerPause,
  playerPlay,
  playerSetFullscreen,
  setStillWatchingHideTimeout,
  setStillWatchingShowTimeout,
  stillWatchingHideCb,
  stillWatchingShowCb,
} from 'helpers/PlayerHelper';
import {
  getLocation,
  helperGetHomeUrl,
  helperGetRelativeUrl,
  helperGoToRelativeUrl,
} from 'helpers/RouteHelper';
import { apiErrorHandler } from 'helpers/ApiHelper';
import { getSessionId } from 'helpers/AuthHelper';
import {
  callSegmentPlayerEvent,
  checkRecommendationIdOnClick,
  Events,
  getEndcardContentList,
  getEndCardPageEventProps,
  getRecommendationIdDataForItem,
  getSegmentLiveViewedVideo,
  getSegmentPodId,
  getSegmentReferrer,
  getSegmentSourceName,
  getSegmentSourceType,
  handleAnalytics,
  PageNames,
  SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
  SegmentPlayerStates,
  SegmentVideoEvents,
  setSegmentContentPersonality,
  trackPageReferrerAnalytics,
  updateNielsenOTTStatus,
} from '@foxcorp/delta-web-analytics';

const BREAKPOINTS = {
  [SITE_PLATFORM_WEB_LARGE]: {
    renderInfoInDetail: false,
    defaultHideAltHeader: false,
    stillWatchingTimeoutEnabled: true,
    showMobileControls: false,
    upNextToastHidesVideoDetail: true,
    playerMetadataOverlay: true,
    fncHideVideoDetails: false,
  },
  [SITE_PLATFORM_WEB]: {
    renderInfoInDetail: false,
    defaultHideAltHeader: false,
    stillWatchingTimeoutEnabled: true,
    showMobileControls: false,
    upNextToastHidesVideoDetail: true,
    playerMetadataOverlay: true,
    fncHideVideoDetails: false,
  },
  [SITE_PLATFORM_TABLET]: {
    renderInfoInDetail: true,
    defaultHideAltHeader: true,
    stillWatchingTimeoutEnabled: false,
    showMobileControls: true,
    upNextToastHidesVideoDetail: false,
    playerMetadataOverlay: false,
    fncHideVideoDetails: true,
  },
  [SITE_PLATFORM_MOBILE]: {
    renderInfoInDetail: true,
    defaultHideAltHeader: true,
    stillWatchingTimeoutEnabled: false,
    showMobileControls: true,
    upNextToastHidesVideoDetail: false,
    playerMetadataOverlay: false,
    fncHideVideoDetails: true,
  },
};

class LiveContext extends Component {
  constructor(props) {
    super(props);
    const geoLocationDeclined = !props.coordinates;
    this.state = {
      contentStarted: false,
      currentVideo: null,
      geoLocationDeclined,
      geoLocationTimeoutFail: false,
      geoLocationLoading: null,
      mediaEndingSeconds: 0,
      secondsViewed: 0,
      showStillWatching: false,
      showEndCards: false,
      // trackedAnalyticsReferrer is used in trackPageReferrerAnalytics
      // eslint-disable-next-line react/no-unused-state
      trackedAnalyticsReferrer: false,
      showUpNextToast: false,
    };
    this.endcardSecondsCountdown = null;
    this.eventVideoProps = null;
    this.exitStreamTimeout = null;
    this.hideUpNextTimeout = null;
    this.intervalFetchUpNext = null;
    this.playbackProps = null;
    this.remainingTimeCountdown = null;
    this.setStillWatchingHideTimeout = setStillWatchingHideTimeout.bind(this);
    this.setStillWatchingShowTimeout = setStillWatchingShowTimeout.bind(this);
    this.isStandaloneLivePage = window.location?.pathname?.includes('/live/');
    this.showUpNextTimeout = null;
    this.stillWatchingHideCb = stillWatchingHideCb.bind(this);
    this.stillWatchingShowCb = stillWatchingShowCb.bind(this);
    this.trackPageReferrerAnalytics = trackPageReferrerAnalytics.bind(this);
    this.AD_INTERVAL_SEC = 1;
  }

  componentDidMount() {
    const { setShouldResetStillWatching, shouldResetStillWatching } = this.props;
    if (shouldResetStillWatching) {
      this.resetStillWatchingTimeout();
      setShouldResetStillWatching(false);
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { nextVideos, nextVideosValidFor, panelVideo } = this.props;
    const {
      nextVideos: newNextVideos,
      nextVideosValidFor: newNextVideosValidFor,
      panelVideo: nextPanelVideo,
    } = nextProps;

    if (nextVideosValidFor !== newNextVideosValidFor && newNextVideosValidFor) {
      this.setIntervalFetchUpNext(newNextVideosValidFor);
    }

    if (nextVideos !== newNextVideos && newNextVideos) {
      this.setShowUpNextTimeout(newNextVideos);
    }

    if (panelVideo && panelVideo !== nextPanelVideo) {
      this.setState({ currentVideo: null });
    }
  }

  componentDidUpdate(prevProps) {
    const { setShouldResetStillWatching, shouldResetStillWatching } = this.props;
    const { shouldResetStillWatching: prevShouldResetStillWatching } = prevProps;
    if (shouldResetStillWatching && !prevShouldResetStillWatching) {
      this.resetStillWatchingTimeout();
      setShouldResetStillWatching(false);
    }
  }

  componentWillUnmount() {
    const currentPlayerState = getPlayerState(getActivePlayer());
    if (currentPlayerState === 'PLAYING' || currentPlayerState === 'AD') {
      callSegmentPlayerEvent({
        eventName: SegmentVideoEvents.videoPlaybackInterrupted,
        props: {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method: INTERRUPTION_METHODS.BROWSER_ACTION,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        },
      });
    }
  }

  getAuthAnalyticsProps = () => {
    const { accessToken, account, anonymousAccount } = this.props;
    const sid = getSessionId(accessToken || account?.accessToken || anonymousAccount?.accessToken);
    return { auth_token_sid: sid };
  };

  onHideUpNextToast = () => {
    const { clearTimeout } = this.props;
    clearTimeout(this.hideUpNextTimeout);
    this.setState({ showUpNextToast: false });
  };

  onStillWatchingClose = () => {
    const { clearTimeout, playerReference, stillWatchingHideTimeout } = this.props;
    clearTimeout(stillWatchingHideTimeout);
    this.resetStillWatchingTimeout();
    this.setState({ showStillWatching: false });
    playerPlay(playerReference);
  };

  onWatchUpNextToast = () => {
    const { clearTimeout, nextVideos, setPlayerState, setVideoPanel } = this.props;
    clearTimeout(this.hideUpNextTimeout);
    const nextVideo = _.get(nextVideos, '1');
    const nextVideoLivePlayerScreenUrl = _.get(nextVideo, 'livePlayerScreenUrl');
    if (!nextVideoLivePlayerScreenUrl) {
      this.setState({ showUpNextToast: false });
      return;
    }
    this.resetStillWatchingTimeout();
    this.setState({ showUpNextToast: false });
    setPlayerState({
      mediaStarted: false,
      isAutoPlay: false,
      firstFramePlayed: false,
    });
    setVideoPanel({ video: nextVideo, source: PLAYER_CONTEXT_LIVE });
  };

  getLiveVideoData() {
    const { fetchedVideo, nextVideos } = this.props;
    if (!_.isEmpty(nextVideos)) {
      return fetchedVideo;
    }
    return getLiveVideo({ nextVideos });
  }

  getEndCardDurationInterval = duration => {
    if (!this.isStandaloneLivePage) return null;
    const { setInterval, clearInterval } = this.props;
    return setInterval(() => {
      const { nextVideos } = this.props;
      const upNextVideo = nextVideos[0];
      const upNextUrl = helperGetRelativeUrl({ url: upNextVideo?.playerScreenUrl });
      if (upNextUrl) browserHistory.push(upNextUrl);
      this.setState({ showEndCards: false });
      clearInterval(this.remainingTimeCountdown);
      clearInterval(this.endcardSecondsCountdown);
    }, duration);
  };

  getEndCardCountdownInterval = () => {
    if (!this.isStandaloneLivePage) return null;
    const { setInterval } = this.props;
    return setInterval(() => {
      const { mediaEndingSeconds } = this.state;
      this.setState({ mediaEndingSeconds: mediaEndingSeconds - 1 });
    }, 1000);
  };

  getPlayerEvents = () => {
    const {
      defaultPlayerEvents,
      fetchedVideo,
      floodlightEvent,
      playerReference,
      saveLiveRestart,
      setPlayerState,
      setVideoStartedPlayback,
    } = this.props;

    const callSign = _.get(fetchedVideo, 'callSign');

    return {
      ...defaultPlayerEvents,
      onReady: contentVideoData => {
        const { video } = this.props;
        const categoryTags = _.get(video, 'categoryTags', false);
        if (categoryTags) {
          setSegmentContentPersonality(categoryTags);
        }
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        this.sessionId = uuidv4();
      },
      onAdPodStart: contentVideoData => {
        console.debug('!!live - onAdPodStart');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodStarted,
          props: data,
        });
        setPlayerState({ adPlaying: true });
      },
      onAdPodComplete: contentVideoData => {
        console.debug('!!live - onAdPodComplete');
        setPlayerState({ adPlaying: false });
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.videoProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdPodCompleted,
          props: data,
        });
      },
      onFirstFrame: async () => {
        console.debug('!!live - onFirstFrame');
        setVideoStartedPlayback(true);
        const {
          account: currentAccount,
          anonymousAccount: currentAnonymousAccount,
          anonymousId: currentAnonymousId,
          anonymousLogin: currentAnonymousLogin,
          segmentReferrer: prevSegmentReferrer,
        } = this.props;
        const loginAnonymousUser =
          !currentAccount &&
          !currentAnonymousAccount &&
          currentAnonymousId &&
          currentAnonymousLogin;
        if (loginAnonymousUser) {
          await currentAnonymousLogin({ deviceId: currentAnonymousId });
        }
        const { segmentSourceName, segmentSourceType } = this.getSegmentSourceData();
        const segmentReferrer = getSegmentReferrer({
          segmentSourceType,
          prevSegmentReferrer,
        });
        setPlayerState({
          mediaComplete: false,
          mediaStarted: true,
          adPodStarted: true,
          clickedEndSlate: false,
          playerLoading: false,
          segmentSourceName,
          segmentSourceType,
          segmentReferrer,
          isVideoOverlayPresent: false,
        });
      },
      onAdLoadStart: () => {
        console.debug('!!live - onAdLoadStart');
      },
      onAdLoadComplete: () => {
        console.debug('!!live - onAdLoadComplete');
      },
      onRemovePlayer: () => {
        setSegmentContentPersonality(false);
      },
      onExitEventStream: e => {
        const { nextVideos } = this.props;
        if (nextVideos?.length && upNextLiveEventsEnabled()) {
          const defaultEndcardDuration = 30;
          const endCardDuration = e?.exitEventStreamSlateDuration || defaultEndcardDuration;
          this.setState({ mediaEndingSeconds: endCardDuration });
          this.endcardSecondsCountdown = this.getEndCardCountdownInterval();
          this.setState({ showEndCards: true });
          const endCardDurationMS = endCardDuration * 1000;
          this.remainingTimeCountdown = this.getEndCardDurationInterval(endCardDurationMS);
        }
      },
      onRestartButtonClicked: () => {
        console.debug('!!live - onRestartButtonClicked');
        const currentVideo = this.getLiveVideoData();
        const playerScreenUrl = _.get(currentVideo, 'playerScreenUrl');
        if (!playerScreenUrl) return;

        playerSetFullscreen(false, playerReference);
        const hashCallSign = callSign ? `-${callSign}` : '';
        saveLiveRestart(currentVideo);
        const watchUrl = helperGetRelativeUrl({ url: playerScreenUrl });
        helperGoToRelativeUrl(`${watchUrl}#${RESTART_TYPE_LIVE}${hashCallSign}`);
      },
      onFullScreen: e => {
        const { fullscreen } = e;
        setPlayerState({ isFullscreen: fullscreen });
        this.videoProps.video_is_fullscreen = fullscreen;
        this.playbackProps.full_screen = fullscreen;
      },
      onChromecastStart: contentVideoData => {
        const castData = {
          displayName: _.get(contentVideoData, 'b.displayName', ''),
          sessionId: _.get(contentVideoData, 'b.sessionId', ''),
          status: _.get(contentVideoData, 'b.status', ''),
        };
        updateNielsenOTTStatus(true, castData);
        floodlightEvent({
          mediaInfo: this.getLiveVideoData(),
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
      },
      onChromecastComplete: () => {
        updateNielsenOTTStatus(false);
        floodlightEvent({
          mediaInfo: this.getLiveVideoData(),
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
      },
      onPlaybackStart: contentVideoData => {
        console.debug('!!live - leapfrog onPlaybackStart');
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        this.videoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        const modelMetaData = contentVideoData?.trackingData?.properties?.modelMetaData;
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          position: 0, // position is always 0 for playback start
          video_player_content_type: null,
          content_asset_id: _.get(contentVideoData, 'trackingData.content_asset_id'),
          content_pod_ids: _.get(contentVideoData, 'trackingData.content_pod_ids'),
          content_pod_id: null,
          rating: _.get(this.videoProps, 'video_rating'),
          session_id: this.sessionId ?? '',
          page_recs_collection_model_id: modelMetaData?.modelId ?? null,
          page_recs_collection_request_id: modelMetaData?.requestId ?? null,
          page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? null,
          page_recs_collection_source_id: modelMetaData?.sourceId ?? null,
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackStart,
          props: data,
        });
      },
      onPlayerError: err => {
        const { contentStarted } = this.state;
        const { panelVideo, setError } = this.props;
        const eventVideoProps = _.get(panelVideo, 'trackingData.video', {});
        const { message } = err || {};
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
        };
        const videoData = {
          video_id: _.get(panelVideo, 'id'),
          video_name: _.get(panelVideo, 'name'),
          ..._.pick(data, [
            'position',
            'livestream',
            'content_pod_id',
            'video_player_state',
            'video_asset_id',
            'video_fox_profile',
            'video_content_length',
            'video_content_type',
            'video_content_length_format',
            'video_station_id',
          ]),
          type: contentStarted ? 'videoStreamInterruptedError' : 'videoStartError',
        };
        const errorObject = new Error(message);
        errorObject.type = contentStarted ? 'videoStreamInterruptedError' : 'videoStartError';
        logPlayerError(errorObject, videoData);
        if (err?.error?.entitlementIssues || err?.data?.entitlementIssues) {
          const errors = getAuthZEntitlementIssues(err);
          setError(errors);
        }
        apiErrorHandler({ error: err?.error ?? err });
      },
      onMediaStart: () => {
        console.debug('!!live - onMediaStart');
        const currentVideo = this.getLiveVideoData();
        floodlightEvent({
          mediaInfo: currentVideo,
          podId: _.get(this.contentProps, 'pod_id', ''),
        });
        const {
          onMediaCompleteData: prevOnMediaCompleteData,
          segmentReferrer: prevSegmentReferrer,
          updateShowcaseVideo,
        } = this.props;
        const { currentVideo: prevCurrentVideo } = this.state;
        const currentVideoId = _.get(currentVideo, 'id');
        const prevVideoId = _.get(prevCurrentVideo, 'id');
        if (currentVideoId !== prevVideoId) {
          this.setState({ currentVideo });
          if (prevCurrentVideo) updateShowcaseVideo(currentVideo);
        }
        const { segmentSourceName, segmentSourceType } = this.getSegmentSourceData();
        const segmentReferrer = getSegmentReferrer({
          segmentSourceType,
          prevSegmentReferrer,
        });
        this.getUpNext(currentVideo);
        setPlayerState(
          {
            clickedEndSlate: false,
            firstFramePlayed: true,
            mediaComplete: false,
            mediaStarted: true,
            onMediaCompleteData: currentVideo,
            playerLoading: false,
            prevOnMediaCompleteData,
            segmentReferrer,
            segmentSourceName,
            segmentSourceType,
          },
          () => {
            const { isAutoPlay } = this.props;
            if (isAutoPlay) {
              this.trackPageReferrerAnalytics({
                name: PageNames.CONTINUOUS_PLAY,
              });
            }
          },
        );
      },
      onMediaPlaying: () => {
        // console.debug('!!live - onMediaPlaying');
      },
      onContentStart: contentVideoData => {
        console.debug('!!live - leapfrog onContentStart');
        const { completedPodId } = this.props;
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const modelMetaData = contentVideoData?.trackingData?.properties?.modelMetaData;
        this.contentProps = _.get(contentVideoData, 'trackingData.content', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          page_recs_collection_model_id: modelMetaData?.modelId ?? null,
          page_recs_collection_request_id: modelMetaData?.requestId ?? null,
          page_recs_collection_result_set_id: modelMetaData?.resultSetId ?? null,
          page_recs_collection_source_id: modelMetaData?.sourceId ?? null,
          ...this.getAuthAnalyticsProps(),
        };
        const videoData = {
          video_id: _.get(contentVideoData, 'id'),
          video_name: _.get(contentVideoData, 'name'),
          ..._.pick(data, [
            'position',
            'livestream',
            'content_pod_id',
            'video_player_state',
            'video_asset_id',
            'video_fox_profile',
            'video_content_length',
            'video_content_type',
            'video_content_length_format',
            'video_station_id',
          ]),
        };
        this.setState({ contentStarted: true });
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentStart,
          props: data,
        });
        logPlayerEvent('onVideoPlaybackStart', videoData);
        if (completedPodId) setPlayerState({ completedPodId: null });
        window?.fennecInstance?.deltaContentStart(contentVideoData);
      },
      onContentPlaying: contentVideoData => {
        const { secondsViewed: previouslyViewed } = this.state;
        const secondsViewed = _.get(contentVideoData, 'trackingData.contentSecondsViewed', 0);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        if (
          secondsViewed !== previouslyViewed &&
          secondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC === 0
        ) {
          const data = {
            ...this.videoProps,
            ...this.contentProps,
            ...eventVideoProps,
            video_seconds_viewed: SEGMENT_CONTENT_PLAYING_INTERVAL_SEC,
            session_id: this.sessionId ?? '',
            ...this.getAuthAnalyticsProps(),
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoContentPlaying,
            props: data,
          });
          window?.fennecInstance?.deltaContentPlaying(contentVideoData);
        }
        if (secondsViewed !== previouslyViewed) {
          const videoPlayhead = { [this.sessionId ?? '']: secondsViewed };
          if (window) _.set(window, '_segHBPlayheads', videoPlayhead);
        }
        this.setState({ secondsViewed });
      },
      onBufferStart: contentVideoData => {
        console.debug('!!live - leapfrog onBufferStart');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferStarted,
          props: data,
        });
      },
      onBufferEnd: contentVideoData => {
        console.debug('!!live - leapfrog onBufferEnd');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackBufferComplete,
          props: data,
        });
      },
      onAdPlaying: contentVideoData => {
        console.debug('!!live - leapfrog onAdPlaying');
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        if (this.newAdTime !== this.previousAdTime && this.newAdTime % this.AD_INTERVAL_SEC === 0) {
          const eventAdProps = _.get(contentVideoData, 'trackingData.ad', {});
          const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
          const content = {
            ..._.pick(this.contentProps, [
              'airdate',
              'asset_id',
              'full_episode',
              'program',
              'title',
            ]),
            ..._.pick(this.videoProps, [
              'load_type',
              'position',
              'total_length',
              'video_content_length',
              'video_nielsen_clientid',
              'video_nielsen_subbrand',
              'video_tms_id',
            ]),
          };
          const props = {
            content,
            ...this.videoProps,
            ...this.adData,
            ...eventAdProps,
            ...eventVideoProps,
            video_seconds_viewed:
              this.newAdTime - (this.newAdTime > this.previousAdTime ? this.previousAdTime : 0),
            session_id: this.sessionId ?? '',
            ...this.getAuthAnalyticsProps(),
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoAdPlaying,
            props,
          });
        }
        this.previousAdTime = this.newAdTime;
      },
      onAdComplete: contentVideoData => {
        console.debug('!!live - leapfrog onAdComplete');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.newAdTime = _.get(contentVideoData, 'trackingData.adSecondsViewed', 0);
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const props = {
          content,
          ...this.videoProps,
          ...this.adData,
          video_seconds_viewed:
            this.newAdTime === this.previousAdTime ? 0 : this.newAdTime - this.previousAdTime,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdCompleted,
          props,
        });
      },
      onAdPause: contentVideoData => {
        console.debug('!!live - leapfrog onAdPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const adVideoProps = _.get(contentVideoData, 'trackingData.ad', {});
        const data = {
          ...this.videoProps,
          ...eventVideoProps,
          ...adVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
      },
      onAdResume: contentVideoData => {
        console.debug('!!live - leapfrog onAdResume');
        this.adData = _.get(contentVideoData, 'trackingData.ad', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.eventProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const data = {
          ...this.eventProps,
          ...this.adData,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoAdResumed,
          props: data,
        });
      },
      onSlateStart: contentVideoData => {
        console.debug('!!live - leapfrog onSlateStart');
        this.slateData = _.get(contentVideoData, 'trackingData.slate', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        const props = {
          content,
          ...this.videoProps,
          ...eventVideoProps,
          ...this.slateData,
          pod_id: 1,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoSlateStarted,
          props,
        });
      },
      onSlatePlaying: contentVideoData => {
        console.debug('!!live - leapfrog onSlatePlaying');
        this.newSlateTime = _.get(contentVideoData, 'trackingData.slateSecondsViewed', 0);
        if (
          this.newSlateTime !== this.previousSlateTime &&
          this.newSlateTime % this.AD_INTERVAL_SEC === 0
        ) {
          const eventSlateProps = _.get(contentVideoData, 'trackingData.slate', {});
          const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
          const content = {
            ..._.pick(this.contentProps, [
              'airdate',
              'asset_id',
              'full_episode',
              'program',
              'title',
            ]),
            ..._.pick(this.videoProps, [
              'load_type',
              'position',
              'total_length',
              'video_content_length',
              'video_nielsen_clientid',
              'video_nielsen_subbrand',
              'video_tms_id',
            ]),
          };
          const props = {
            content,
            ...this.videoProps,
            ...this.slateData,
            ...eventSlateProps,
            ...eventVideoProps,
            pod_id: 1,
            video_seconds_viewed:
              this.newSlateTime -
              (this.newSlateTime > this.previousSlateTime ? this.previousSlateTime : 0),
            session_id: this.sessionId ?? '',
            ...this.getAuthAnalyticsProps(),
          };
          callSegmentPlayerEvent({
            eventName: SegmentVideoEvents.videoSlatePlaying,
            props,
          });
        }
        this.previousSlateTime = this.newSlateTime;
      },
      onSlateComplete: contentVideoData => {
        console.debug('!!live - leapfrog onSlateComplete');
        this.slateData = _.get(contentVideoData, 'trackingData.slate', {});
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.newSlateTime = _.get(contentVideoData, 'trackingData.slateSecondsViewed', 0);
        this.videoProps = {
          ...this.videoProps,
          ...eventVideoProps,
        };
        const content = {
          ..._.pick(this.contentProps, ['airdate', 'asset_id', 'full_episode', 'program', 'title']),
          ..._.pick(this.videoProps, [
            'load_type',
            'position',
            'total_length',
            'video_content_length',
            'video_nielsen_clientid',
            'video_nielsen_subbrand',
            'video_tms_id',
          ]),
        };
        const props = {
          content,
          ...this.videoProps,
          ...this.slateData,
          pod_id: 1,
          video_seconds_viewed:
            this.newSlateTime === this.previousSlateTime
              ? 0
              : this.newSlateTime - this.previousSlateTime,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoSlateCompleted,
          props,
        });
      },
      onPause: contentVideoData => {
        console.debug('!!live - leapfrog onPause');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackPaused,
          props: data,
        });
        clearInterval(this.endcardSecondsCountdown);
        clearInterval(this.remainingTimeCountdown);
      },
      onResume: contentVideoData => {
        console.debug('!!live - leapfrog onResume');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        this.playbackProps = _.get(contentVideoData, 'trackingData.playback', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackResumed,
          props: data,
        });
      },
      onContentComplete: contentVideoData => {
        console.debug('!!live - leapfrog onContentComplete');
        const totalSecondsViewed = _.get(contentVideoData, 'trackingData.contentSecondsViewed', 0);
        const secondsViewed = Math.floor(totalSecondsViewed % SEGMENT_CONTENT_PLAYING_INTERVAL_SEC);
        const completedPodId = getSegmentPodId(contentVideoData);
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.contentProps,
          ...eventVideoProps,
          video_seconds_viewed: secondsViewed,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        setPlayerState({ completedPodId });
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoContentComplete,
          props: data,
        });
        this.setState({ contentStarted: false });
      },
      onPlaybackComplete: contentVideoData => {
        console.debug('!!live - leapfrog onPlaybackComplete');
        const eventVideoProps = _.get(contentVideoData, 'trackingData.video', {});
        const data = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          ...eventVideoProps,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };

        data.video_player_content_type = null;
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackComplete,
          props: data,
        });
        setPlayerState({ completedPodId: null });
      },
      onPlaybackInterrupted: data => {
        console.debug('!!live - leapfrog onPlaybackInterrupted');
        const { method } = data;
        const props = {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        };
        callSegmentPlayerEvent({
          eventName: SegmentVideoEvents.videoPlaybackInterrupted,
          props,
        });
      },
      onMediaComplete: () => {
        console.debug('!!live - onMediaComplete');
        setPlayerState(
          {
            mediaComplete: true,
            mediaEnding: false,
            isAutoPlay: true,
            mediaEndingSeconds: null,
            firstFramePlayed: true,
          },
          () => {
            this.clearIntervalFetchUpNext();
            this.sendNextVideo();
          },
        );
      },
      onUserInteraction: () => {
        this.resetStillWatchingTimeout();
      },
      onVolumeChange: ({ newVolume, oldVolume }) => {
        const { video_volume } = this.videoProps;
        if (video_volume === newVolume || oldVolume === newVolume) return null;
        this.videoProps.video_volume = newVolume;
        this.playbackProps.sound = newVolume;
        return null;
      },
    };
  };

  getSegmentSourceData = () => {
    const video = this.getLiveVideoData();
    const { isAutoPlay: autoplay, preauthorizedResources, prevVideo } = this.props;
    const location = getLocation(window.referrer);
    const { pathname: path } = location;
    const segmentSourceType = getSegmentSourceType({
      homeUrl: helperGetHomeUrl(),
      path,
      autoplay,
      epg: true,
    });
    const data = getSegmentLiveViewedVideo({
      video,
      prevVideo,
      segmentSourceType,
      preauthorizedResources,
    });
    const segmentSourceName = getSegmentSourceName({
      data,
      type: segmentSourceType,
    });
    return {
      segmentSourceName,
      segmentSourceType,
    };
  };

  getUpNext(video) {
    const { accessToken, fetchUpNext } = this.props;
    const videoId = _.get(video, 'id');
    if (videoId) fetchUpNext({ accessToken, videoId });
  }

  setIntervalFetchUpNext(validFor) {
    const { setInterval } = this.props;
    this.clearIntervalFetchUpNext();
    this.intervalFetchUpNext = setInterval(() => {
      const currentVideo = this.getLiveVideoData();
      this.getUpNext(currentVideo);
    }, validFor);
  }

  setShowUpNextTimeout(nextVideos) {
    const { clearTimeout, playerType, setTimeout } = this.props;
    if (!UpNextToastEnabled || isPlayerTypeHero(playerType)) return;
    clearTimeout(this.showUpNextTimeout);
    const currentEndDate = _.get(nextVideos, '0.endDate', null);
    const nextStartDate = _.get(nextVideos, '1.startDate', null);
    const currentEnd = currentEndDate && stringToDate(currentEndDate);
    const nextStart = nextStartDate && stringToDate(nextStartDate);
    const hasOverlap = currentEnd && nextStart && isBefore(nextStart, currentEnd);

    if (!hasOverlap) return;

    const { toastDisplayBeforeEndInMinutes: minutesToSubtract } = window.ExternalConfig;
    const showTime = subMinutes(nextStart, minutesToSubtract);
    const showInMs = differenceInMilliseconds(showTime, now());
    if (showInMs < 0) return;
    this.showUpNextTimeout = setTimeout(() => {
      this.cbShowUpNextToast();
    }, showInMs);
  }

  callbackShow = () => {
    const {
      account,
      breakpoint,
      clearTimeout,
      firstFramePlayed,
      playerReference,
      setPlayerState,
      stillWatchingShowTimeout,
    } = this.props;
    const profileId = _.get(account, 'profileId', null);
    const { stillWatchingTimeoutEnabled } = BREAKPOINTS[breakpoint];

    clearTimeout(stillWatchingShowTimeout);
    if (stillWatchingTimeoutEnabled && firstFramePlayed) {
      playerSetFullscreen(false, playerReference);
      playerPause(playerReference);
      playerHideControls(playerReference);
      this.stillWatchingShowCb({ profileId });
      const stillWatchingHideTimeout = this.setStillWatchingHideTimeout({
        cb: () => {
          playerPlay(playerReference);
          this.stillWatchingHideCb();
          window.location = helperGetHomeUrl();
        },
      });
      setPlayerState({ stillWatchingHideTimeout });
    }
  };

  locationCallback = (error, coordinates) => {
    const { setCoordinates } = this.props;

    if (error) {
      setCoordinates(null);
      const errorCode = _.get(error, 'code');
      const errorMessage = _.get(error, 'message');
      const didTimeout = errorCode === 3 || _.includes(_.toLower(errorMessage), 'time');
      this.setState({
        geoLocationDeclined: true,
        geoLocationLoading: false,
        geoLocationTimeoutFail: didTimeout,
      });
    }

    if (coordinates) {
      setCoordinates(coordinates);
      this.setState({ geoLocationDeclined: false, geoLocationLoading: false });
    }
  };

  requestLocation = () => {
    getGeoLocation((error, coordinates) => {
      if (error) this.locationCallback(error, null);
      if (coordinates) this.locationCallback(null, coordinates);
    });
  };

  resetStillWatchingTimeout = () => {
    const {
      breakpoint,
      clearTimeout,
      firstFramePlayed,
      setPlayerState,
      stillWatchingShowTimeout,
    } = this.props;
    clearTimeout(stillWatchingShowTimeout);
    const { stillWatchingTimeoutEnabled } = BREAKPOINTS[breakpoint];

    const debugDisabledTimeout = getCookie({ name: DEBUG_PANEL_TIMEOUT_DISABLED });
    const durationSeconds = _.get(window.ExternalConfig, 'stillWatching.liveLimitSeconds');
    if (stillWatchingTimeoutEnabled && firstFramePlayed && debugDisabledTimeout !== 'YES') {
      const duration = durationSeconds && durationSeconds * 1000;
      const newStillWatchingShowTimeout = this.setStillWatchingShowTimeout({
        cb: this.callbackShow,
        duration,
      });
      setPlayerState({ stillWatchingShowTimeout: newStillWatchingShowTimeout });
    }
  };

  cbShowUpNextToast() {
    const { clearTimeout, setTimeout } = this.props;
    const { toastDisplayDurationInSeconds } = window.ExternalConfig;
    this.setState({ showUpNextToast: true });

    clearTimeout(this.hideUpNextTimeout);
    this.hideUpNextTimeout = setTimeout(() => {
      this.setState({ showUpNextToast: false });
    }, toastDisplayDurationInSeconds * 1000);
  }

  clearIntervalFetchUpNext() {
    const { clearInterval, clearNextVideosValidFor } = this.props;
    clearInterval(this.intervalFetchUpNext);
    clearNextVideosValidFor();
  }

  sendNextVideo() {
    const { playerType, setVideoPanel } = this.props;
    if (!UpNextStreamOverrideEnabled || isPlayerTypeHero(playerType)) return;
    const { nextVideos } = this.props;
    const currentVideo = _.get(nextVideos, '0');
    const nextVideo = _.get(nextVideos, '1');
    if (!currentVideo || !nextVideo) return;
    const network = _.get(nextVideo, 'network');
    const hasSportsContent = isSportsContent(currentVideo) || isSportsContent(nextVideo);
    if (network !== FOX || !hasSportsContent) return;
    setVideoPanel({ video: nextVideo, source: PLAYER_CONTEXT_LIVE });
  }

  onEndcardClick = opts => {
    const { nextVideos } = this.props;
    const { itemIndex, subtitleText, titleText, originalItem } = opts;
    let endcardContentLength = 0;
    this.endcardItem = originalItem;
    if (nextVideos.length) {
      endcardContentLength = nextVideos.length > 1 ? 2 : nextVideos.length;
    }
    handleAnalytics({
      event: Events.END_CARD_ITEM_SELECTED,
      payload: {
        page_action_type: itemIndex === 0 ? 'upNext' : 'recommended',
        page_item_title: titleText || undefined,
        page_item_subtitle: subtitleText || undefined,
        end_card_content_list: getEndcardContentList({
          endcardItems: nextVideos,
          headerRecommended: window.Language?.videoPage_headerRecommended ?? '',
          headerUpNext: window.Language?.videoPage_headerUpNext ?? '',
        }),
        end_card_content_length: endcardContentLength,
        end_card_selected_position: itemIndex === 0 ? 1 : itemIndex + 1,
        ...getEndCardPageEventProps({
          endcardItems: [originalItem],
          contentLength: 1,
        }),
      },
      tealiumPayload: {
        ...getCommonTealiumProps(),
      },
    });
    const { setPlayerState } = this.props;
    setPlayerState({ clickedEndSlate: true }, () => {
      const { recommendationIdData, setRecommendationId } = this.props;
      const nextRecommendationIdData = getRecommendationIdDataForItem(originalItem);

      this.trackPageReferrerAnalytics({
        name: PageNames.ENDCARD,
      });

      // Update recommendation id on click of endcard item if they are not the same
      checkRecommendationIdOnClick(
        recommendationIdData,
        nextRecommendationIdData,
        setRecommendationId,
      );
    });
  };

  renderEndcards() {
    const {
      breakpoint,
      continuousPlayback,
      isAuthenticated,
      isFullscreen,
      nextVideos,
      playerType,
      video,
    } = this.props;
    const { showEndCards, mediaEndingSeconds } = this.state;
    const shouldDisplayEndcard = this.isStandaloneLivePage && showEndCards;
    const shouldRender = shouldDisplayEndcard && !isPlayerTypeHero(playerType);

    return shouldRender ? (
      <Endcard
        continuousPlayback={continuousPlayback}
        endcardItems={nextVideos}
        renderImage={image =>
          renderImage({
            breakpoint,
            imageType: WATCH_RELATED_THUMBNAIL_IMAGE,
            ...image,
          })
        }
        video={video}
        mediaEndingSeconds={mediaEndingSeconds}
        videoProcessor={endcardVideoProcessor}
        isAuthenticated={isAuthenticated}
        isFullscreen={isFullscreen}
        onClick={this.onEndcardClick}
      />
    ) : null;
  }

  renderVideoDetail(opts = {}) {
    const {
      adPlaying,
      breakpoint,
      creditsPlaying,
      favoritesManager,
      isContextPlayer,
      isFullscreen,
      panelVideo,
      playerType,
      setPlayerDataHover,
      setStickyControls,
      theme,
      video,
    } = this.props;
    const { currentVideo, showEndCards } = this.state;
    const { renderInfoInDetail, fncHideVideoDetails } = BREAKPOINTS[breakpoint];
    const { showControls, isFNC } = opts;
    const classes = classNames(
      isFullscreen ? theme.fullscreen : false,
      theme.watchDetailContainer,
      isEpgv2Enabled() && theme.epgLivePageWatchDetailContainer,
      showEndCards && theme.hide,
    );
    if (isPlayerTypeHero(playerType)) return null;

    const renderVideoDetail = () => (
      <div className={classes}>
        <VideoDetail
          {...getVideoDetailProps(currentVideo || panelVideo)}
          adPlaying={adPlaying}
          favoritesManager={favoritesManager}
          isContextPlayer={isContextPlayer}
          isLive
          renderInfo={renderInfoInDetail}
          setPlayerDataHover={setPlayerDataHover}
          setStickyControls={setStickyControls}
          showControls={showControls}
          showNetworkIcon={!creditsPlaying}
          video={video}
        />
      </div>
    );

    return isFNC && fncHideVideoDetails ? null : renderVideoDetail();
  }

  render() {
    const {
      adPlaying,
      breakpoint,
      coordinates,
      fetchedVideo,
      nextVideos,
      panelVideo,
      playerDataHover,
      playerLoading,
      playerType,
      renderVideoContainer,
      setLoading,
      subscriptionPaused,
      showControls,
      theme,
      video,
      isFullscreen,
      containerId,
    } = this.props;
    const {
      geoLocationDeclined,
      geoLocationLoading,
      geoLocationTimeoutFail,
      showStillWatching,
      showUpNextToast,
    } = this.state;
    const callSign = _.get(fetchedVideo, 'callSign');
    const guid = _.get(fetchedVideo, 'guid');
    const livePlayerScreenUrl = _.get(fetchedVideo, 'livePlayerScreenUrl');
    const { showMobileControls, upNextToastHidesVideoDetail } = BREAKPOINTS[breakpoint];
    if (isPlayerTypeHero(playerType) && geolocationEnabled() && !coordinates) return null;
    if (geoLocationLoading === true) return <Loader setLoading={setLoading} center />;
    if (geoLocationDeclined === null) return <div className={theme.playerSpacer} />;
    const liveContentId = _.last(_.split(livePlayerScreenUrl, '/'));
    const isNationDigital = isFNCv2Enabled() && callSign === 'NATION-DIGITAL';
    const isMobile = isMobileBreakpoint(breakpoint);
    const seriesBackground = _.get(
      panelVideo,
      'images.videoList.raw',
      _.get(panelVideo, 'images.still.raw', _.get(panelVideo, 'images.seriesList.raw')),
    );
    const fncBackground = _.get(
      window.ExternalConfig,
      'nationCurtain.background.desktop',
      seriesBackground,
    );
    const fncBackgroundMobile = _.get(
      window.ExternalConfig,
      'nationCurtain.background.mobile',
      seriesBackground,
    );
    const nationBackground = isMobile ? fncBackgroundMobile : fncBackground;
    const url = isNationDigital ? nationBackground : seriesBackground;
    const background = getDynamicImage({
      breakpoint,
      context: LIVE_PLAYER_BACKGROUND_IMAGE,
      url,
    });
    const currentVideo = this.getLiveVideoData();
    const backfade =
      ((showControls || playerDataHover || playerLoading) && !adPlaying) || showUpNextToast;
    const showVideoDetail =
      (showControls ||
        (playerDataHover && !isFullscreen) ||
        (showMobileControls && !isFullscreen)) &&
      currentVideo &&
      !playerLoading;

    const contextProps = {
      backfade,
      background,
      callSign,
      channel: callSign,
      containerId,
      geoLocationDeclined: geolocationEnabled() && geoLocationDeclined && !coordinates,
      geoLocationTimeoutFail,
      geoLocationPrimaryActionCallback: geolocationEnabled() ? this.requestLocation : null,
      guid,
      isFullscreen,
      isLive: true,
      liveContentId,
      mediaInfo: video,
      mode: LIVE,
      onStillWatchingClose: this.onStillWatchingClose,
      playerConfig: this.getPlayerEvents(),
      playerLoading,
      playerLoadingImage: background,
      showControls: showVideoDetail,
      showStillWatching,
      showUpNextToast,
      subscriptionPaused,
      type: playerType,
    };

    const currentPlayerState = getPlayerState(getActivePlayer());
    if (!this.currentChannel) this.currentChannel = callSign;
    if (
      this.currentChannel &&
      this.currentChannel !== callSign &&
      (currentPlayerState === 'PLAYING' || currentPlayerState === 'AD')
    ) {
      this.currentChannel = callSign;
      callSegmentPlayerEvent({
        eventName: SegmentVideoEvents.videoPlaybackInterrupted,
        props: {
          ...this.videoProps,
          ...this.playbackProps,
          ...this.contentProps,
          method: INTERRUPTION_METHODS.BROWSER_ACTION,
          video_player_state: SegmentPlayerStates.PAUSE,
          session_id: this.sessionId ?? '',
          ...this.getAuthAnalyticsProps(),
        },
      });
    }

    const nextVideo = _.get(nextVideos, '1', null);
    const hideVideoDetail = showUpNextToast && upNextToastHidesVideoDetail;
    const renderVideoDetail = showVideoDetail && !hideVideoDetail;
    const endCards = this.renderEndcards();

    return (
      <>
        {renderVideoContainer({ props: contextProps, endCards })}
        {this.renderVideoDetail({ showControls: renderVideoDetail, isFNC: isFNCv2Enabled() })}
        {showUpNextToast && nextVideo && !isPlayerTypeHero(playerType) && (
          <UpNextToast
            onWatch={this.onWatchUpNextToast}
            onHide={this.onHideUpNextToast}
            renderImage={image =>
              renderImage({
                breakpoint,
                imageType: LIVE_EPG_THUMBNAIL_IMAGE,
                ...image,
              })
            }
            video={nextVideo}
          />
        )}
      </>
    );
  }
}

LiveContext.defaultProps = {
  accessToken: null,
  account: null,
  adPlaying: null,
  anonymousAccount: {},
  anonymousId: null,
  anonymousLogin: noop,
  clickedThumbnail: null,
  completedPodId: null,
  containerId: null,
  continuous: null,
  continuousPlayback: null,
  coordinates: null,
  creditsPlaying: null,
  errors: null,
  favoritesManager: null,
  fetchedVideo: null,
  firstAdPlayed: null,
  firstFramePlayed: null,
  isAuthenticated: null,
  isAutoPlay: null,
  isContextPlayer: false,
  isFullscreen: null,
  location: null,
  mediaComplete: null,
  mediaEnding: null,
  mediaEndingSeconds: null,
  nextVideosValidFor: null,
  onMediaCompleteData: null,
  panelVideo: null,
  playerLoading: null,
  playlist: null,
  preauthorizedResources: null,
  prevOnMediaCompleteData: null,
  prevVideo: null,
  provider: null,
  recommendationIdData: null,
  restart: null,
  restartPlayerScreenUrl: null,
  restartVideo: null,
  saveLiveRestart: noop,
  segmentReferrer: null,
  segmentSourceName: null,
  segmentSourceType: null,
  setLoading: noop,
  setRecommendationId: noop,
  shouldResetStillWatching: null,
  startedPlayback: null,
  stillWatchingHideTimeout: noop,
  stillWatchingShowTimeout: null,
  video: null,
};

LiveContext.propTypes = {
  accessToken: PropTypes.string,
  account: PropTypes.shape({}),
  adPlaying: PropTypes.bool,
  anonymousAccount: PropTypes.shape({}),
  anonymousId: PropTypes.string,
  anonymousLogin: PropTypes.func,
  breakpoint: PropTypes.string.isRequired,
  clearInterval: PropTypes.func.isRequired,
  clearNextVideosValidFor: PropTypes.func.isRequired,
  clearTimeout: PropTypes.func.isRequired,
  clickedThumbnail: PropTypes.bool,
  completedPodId: PropTypes.string,
  containerId: PropTypes.string,
  continuous: PropTypes.bool,
  continuousPlayback: PropTypes.bool,
  coordinates: PropTypes.shape({}),
  creditsPlaying: PropTypes.bool,
  defaultPlayerEvents: PropTypes.shape({}).isRequired,
  errors: PropTypes.number,
  favoritesManager: PropTypes.shape({}),
  fetchUpNext: PropTypes.func.isRequired,
  fetchedVideo: PropTypes.shape({}),
  firstAdPlayed: PropTypes.bool,
  firstFramePlayed: PropTypes.bool,
  floodlightEvent: PropTypes.func.isRequired,
  getUseUtagGlobal: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool,
  isAutoPlay: PropTypes.bool,
  isContextPlayer: PropTypes.bool,
  isFullscreen: PropTypes.bool,
  location: PropTypes.shape({}),
  mediaComplete: PropTypes.bool,
  mediaEnding: PropTypes.bool,
  mediaEndingSeconds: PropTypes.number,
  nextVideos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  nextVideosValidFor: PropTypes.number,
  onMediaCompleteData: PropTypes.shape({}),
  openSignIn: PropTypes.func.isRequired,
  openSignUp: PropTypes.func.isRequired,
  pageViewEvent: PropTypes.func.isRequired,
  panelVideo: PropTypes.shape({}),
  playerDataHover: PropTypes.bool.isRequired,
  playerLoading: PropTypes.bool,
  playerReference: PropTypes.string,
  playerType: PropTypes.string.isRequired,
  playlist: PropTypes.bool,
  preauthorizedResources: PropTypes.arrayOf(PropTypes.string),
  prevOnMediaCompleteData: PropTypes.shape({}),
  prevVideo: PropTypes.shape({}),
  provider: PropTypes.shape({}),
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  renderVideoContainer: PropTypes.func.isRequired,
  restart: PropTypes.bool,
  restartPlayerScreenUrl: PropTypes.string,
  restartVideo: PropTypes.shape({}),
  saveLiveRestart: PropTypes.func,
  segmentReferrer: PropTypes.shape({}),
  segmentSourceName: PropTypes.string,
  segmentSourceType: PropTypes.string,
  setCoordinates: PropTypes.func.isRequired,
  setError: PropTypes.func,
  setInterval: PropTypes.func.isRequired,
  setLoading: PropTypes.func,
  setPlayerDataHover: PropTypes.func.isRequired,
  setPlayerState: PropTypes.func.isRequired,
  setRecommendationId: PropTypes.func,
  setShouldResetStillWatching: PropTypes.func,
  setStickyControls: PropTypes.func.isRequired,
  setStreamExit: PropTypes.func.isRequired,
  setTimeout: PropTypes.func.isRequired,
  setVideoPanel: PropTypes.func.isRequired,
  setVideoStartedPlayback: PropTypes.func.isRequired,
  shouldResetStillWatching: PropTypes.bool,
  showControls: PropTypes.bool.isRequired,
  startedPlayback: PropTypes.bool,
  stillWatchingHideTimeout: PropTypes.func,
  stillWatchingShowTimeout: PropTypes.number,
  subscriptionPaused: PropTypes.bool.isRequired,
  theme: PropTypes.shape({}).isRequired,
  updateShowcaseVideo: PropTypes.func,
  video: PropTypes.shape({}),
};

export default ReactTimeout(LiveContext);
